import React, { useState } from 'react';
import { Copy, EyeOff, Globe, RefreshCw, XCircle } from 'react-feather';
import { Link } from 'react-router-dom';
import { api, Question } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';

interface Props {
  question: Question;
  onClose: () => void;
  onUpdate: (question: Question) => void;
}

const ShareModal: React.FC<Props> = ({ question, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [sharing, setSharing] = useState(false);
  const [unsharing, setUnsharing] = useState(false);

  const isPro = user?.plan === 'pro';
  const shareUrl = question.share_slug
    ? `${window.location.origin}/q/${question.share_slug}`
    : null;

  const handleShare = async () => {
    try {
      setSharing(true);
      const updated = await api.shareQuestion(question.id);
      onUpdate(updated);
      showSnackbar(
        question.share_slug
          ? 'New public link created. The old link no longer works.'
          : 'Question is now publicly visible.',
        { variant: 'success' },
      );
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to share question', { variant: 'error' });
    } finally {
      setSharing(false);
    }
  };

  const handleUnshare = async () => {
    try {
      setUnsharing(true);
      const updated = await api.unshareQuestion(question.id);
      onUpdate(updated);
      showSnackbar('Question is no longer publicly visible.', { variant: 'success' });
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to stop sharing', { variant: 'error' });
    } finally {
      setUnsharing(false);
    }
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl)
      .then(() => showSnackbar('Public link copied.', { variant: 'success' }))
      .catch(() => showSnackbar('Failed to copy link.', { variant: 'error' }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Share publicly</h2>
          <button className="btn-close" onClick={onClose}><XCircle size={18} /></button>
        </div>

        <div className="modal-body">
          {shareUrl ? (
            <>
              <p className="text-muted">
                Anyone with this link can view the question, its variables,
                correlations, and recent data points — no account required.
              </p>
              <div className="share-url-row">
                <input className="share-url-input" type="text" readOnly value={shareUrl} onFocus={e => e.target.select()} />
                <button className="btn btn-secondary btn-sm" onClick={handleCopy}>
                  <Copy size={14} />
                  Copy
                </button>
              </div>
              <div className="form-actions">
                <button className="btn btn-secondary btn-sm" onClick={handleShare} disabled={sharing || unsharing}>
                  <RefreshCw size={14} />
                  {sharing ? 'Creating…' : 'New link'}
                </button>
                <button className="btn btn-danger btn-sm" onClick={handleUnshare} disabled={sharing || unsharing}>
                  <EyeOff size={14} />
                  {unsharing ? 'Stopping…' : 'Stop sharing'}
                </button>
              </div>
              <p className="text-muted share-hint">
                Creating a new link or stopping sharing makes previously shared
                links stop working.
              </p>
            </>
          ) : isPro ? (
            <>
              <p className="text-muted">
                Create a public link so anyone can view this question, its
                variables, correlations, and recent data points — no account
                required. You can stop sharing at any time.
              </p>
              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleShare} disabled={sharing}>
                  <Globe size={14} />
                  {sharing ? 'Creating…' : 'Create public link'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-muted">
                Public sharing is available on the Pro plan. Upgrade to publish
                this question on a public page that anyone can view.
              </p>
              <div className="form-actions">
                <Link to="/profile" className="btn btn-primary" onClick={onClose}>
                  Upgrade to Pro
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
